import { logger } from "../../utils/logger";

import { ChainCodeErrorEnum } from "../../enums/rpcErrorCodes";
import { toTokenUnits } from "../../utils/decimalString";
import {
  getInsufficientGasMessage,
  isInsufficientGasError,
} from "../../utils/txError";
import { executeDeposit } from "./executeDeposit";
import { RunDepositOrApproveParams } from "./types";

// Extracted verbatim from ConnectWalletModal's `handleDepositOrApprove`
// useCallback body. The single local `insufficientLimit` closure and the
// deposit branch were relocated (helpers.ts / executeDeposit.tsx); control
// flow is unchanged — the deposit branch still runs inside this try/catch.
export const runDepositOrApprove = async (
  data: { amount: string },
  params: RunDepositOrApproveParams
) => {
  const {
    depositActionInFlightRef,
    setLoading,
    setPageError,
    setDepositError,
    depositReady,
    useUnifiedVaultFlow,
    isNativeDepositAsset,
    setUnifiedVaultApprovalChecked,
    decimals,
    isUnifiedVaultApproved,
    approveUnifiedVaultUSDC,
    setShowReadyToDepositMessage,
    l1DepositHelper,
    getAssetPermitSignature,
    getTimestamp,
    setApprovedAmountAndSignature,
    approvalSpender,
    approveAsset,
    setAssetAllowance,
    t,
    setPendingDepositTxURL,
  } = params;

  if (depositActionInFlightRef.current) {
    return;
  }

  depositActionInFlightRef.current = true;
  try {
    // Loading
    setLoading(true);
    setPageError("");
    setDepositError("");

    // Floored to the token's decimals; `parseUnits` on the typed text threw a
    // raw ethers error for exponent text or one decimal too many.
    const amountUnits = toTokenUnits(data.amount, decimals);
    if (!amountUnits || amountUnits.isZero()) {
      setDepositError(t("invalid_amount", { defaultValue: "Enter a valid amount" }));
      return;
    }

    //
    /**
     * If deposit is not ready:
     * 1. For UnifiedVault flow (BASECAMP_TESTNET): approve USDC for UnifiedVault
     * 2. If can permit, ask for permit signature
     * 3. Else, ask to send asset approval
     */
    if (!depositReady) {
      // UnifiedVault / BalanceManager approval flow
      if (useUnifiedVaultFlow) {
        if (isNativeDepositAsset) {
          setUnifiedVaultApprovalChecked(true);
          return;
        }
        // Check on-chain allowance first — skip approval if already sufficient
        const amountWei = amountUnits.toString();
        const alreadyApproved = await isUnifiedVaultApproved(amountWei);
        if (!alreadyApproved) {
          await approveUnifiedVaultUSDC();
        }
        setUnifiedVaultApprovalChecked(true);
        // AUTO-CONTINUE — ONLY after a SUCCESSFUL approval. If the approve above
        // fails (user rejects, RPC error, or the tx reverts) then
        // approveUnifiedVaultUSDC() THROWS (it awaits tx.wait(1)), so execution
        // jumps to the catch below and we NEVER reach this deposit. We only get
        // here when the allowance is confirmed mined on-chain (or was already
        // sufficient), so executeDeposit won't fail on allowance. This carries
        // the user straight from the approval into the deposit, past Privy's
        // post-approve "Transaction complete / All Done" screen — so they're not
        // left thinking the whole deposit is done when only the approval was.
        //
        // We deliberately do NOT setShowReadyToDepositMessage(true) here: that
        // renders "Approved — now click Deposit" (t: ready_to_deposit), which
        // would contradict the auto-continue by telling the user to click a
        // button that is already firing. That message stays for the permit /
        // approvalSpender branches below, which DO stop for a manual click.
        await executeDeposit(data, params);
        return;
      } else if (l1DepositHelper && getAssetPermitSignature !== undefined) {
        // 1 hour from now
        const deadline = getTimestamp() + 60 * 60;
        const signature = await getAssetPermitSignature(
          l1DepositHelper.address,
          data.amount,
          deadline
        );

        setApprovedAmountAndSignature({
          signature,
          deadline,
          approvedAmount: data.amount,
        });
        setShowReadyToDepositMessage(true);
        return;
      } else if (approvalSpender) {
        await approveAsset(approvalSpender, data.amount);
        setAssetAllowance(data.amount);
        setShowReadyToDepositMessage(true);
        return;
      }
    }

    await executeDeposit(data, params);
  } catch (error: any) {
    console.error("[DEPOSIT ERROR] Full error details:", {
      errorMessage: error?.message,
      errorCode: error?.code,
      errorData: error?.data,
      errorReason: error?.reason,
      errorStack: error?.stack,
      fullError: error,
    });

    const errMsg: string =
      (error?.message || error?.data?.message || "").toString().toLowerCase();
    const userRejected =
      error?.code === ChainCodeErrorEnum.CANCELLED ||
      error?.code === "ACTION_REJECTED" ||
      errMsg.includes("user rejected") ||
      errMsg.includes("user denied") ||
      errMsg.includes("rejected the request");
    // The token amount is validated before submit, so an insufficient balance
    // here means the wallet can't pay the network GAS fee (native token).
    const insufficientGas = isInsufficientGasError(error);
    if (userRejected) {
      setPageError(t("deposit_cancelled"));
    } else if (insufficientGas) {
      setPageError(getInsufficientGasMessage());
    } else {
      setPageError(t("deposit_failed"));
      // eslint-disable-next-line no-console
      logger.error({ error });
    }
  } finally {
    setLoading(false);
    setPendingDepositTxURL(undefined);
    depositActionInFlightRef.current = false;
  }
};
